"use client"

import { useState, useEffect } from "react"
import "./ServiceCategoryManager.css"
import serviceCategoryService from "../../../services/api/serviceCategoryService"

export default function ServiceCategoryManagement() {
    // State for categories list
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // State for form inputs
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        photo: "/placeholder.svg?height=100&width=300"
    })

    // State for views
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [showDetail, setShowDetail] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories()
    }, [])

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            setLoading(true)
            const response = await serviceCategoryService.getAllCategories()
            setCategories(Array.isArray(response) ? response : [])
        } catch (error) {
            console.error("Lỗi khi tải danh sách danh mục:", error)
            setError("Không thể tải danh sách danh mục dịch vụ")
            setCategories([])
        } finally {
            setLoading(false)
        }
    }

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setFormData({
                        ...formData,
                        photo: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            await serviceCategoryService.createCategory(formData)
            await fetchCategories()
            // Reset form
            setFormData({
                name: "",
                description: "",
                photo: "/placeholder.svg?height=100&width=300"
            })
            // Hide the form after submission
            setShowAddForm(false)
        } catch (error) {
            console.error("Lỗi khi tạo danh mục:", error)
            setError("Không thể tạo danh mục dịch vụ mới")
        } finally {
            setLoading(false)
        }
    }

    // Handle category deletion
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa danh mục này không?")) {
            try {
                setLoading(true)
                await serviceCategoryService.deleteCategory(id)
                await fetchCategories()
                if (selectedCategory && selectedCategory._id === id) {
                    setShowDetail(false)
                    setSelectedCategory(null)
                }
            } catch (error) {
                console.error("Lỗi khi xóa danh mục:", error)
                setError("Không thể xóa danh mục dịch vụ")
            } finally {
                setLoading(false)
            }
        }
    }

    // Handle view details
    const handleViewDetails = async (category) => {
        try {
            setLoading(true)
            console.log('Đang tải thông tin chi tiết danh mục:', category)
            const categoryDetails = await serviceCategoryService.getCategoryById(category._id)
            console.log('Đã nhận thông tin chi tiết danh mục:', categoryDetails)
            setSelectedCategory(categoryDetails)
            setShowDetail(true)
        } catch (error) {
            console.error("Lỗi khi tải thông tin chi tiết danh mục:", error)
            console.error("Chi tiết lỗi:", error.response?.data || error.message)
            setError("Không thể tải thông tin chi tiết danh mục dịch vụ")
        } finally {
            setLoading(false)
        }
    }

    // Toggle add form
    const toggleAddForm = () => {
        setShowAddForm(!showAddForm)
    }

    return (
        <div className="scm-container">
            {!showDetail ? (
                <>
                    <h1 className="scm-heading">Quản Lý Danh Mục Dịch Vụ</h1>
                    {error && <div className="error-message">{error}</div>}
                    {loading && <div className="loading-spinner">Đang tải...</div>}
                    <div className="scm-layout">
                        <div className="scm-list-container">
                            <div className="scm-list-header">
                                <h2 className="scm-subheading">Danh Sách Danh Mục Dịch Vụ</h2>
                                <button className="scm-add-button" onClick={toggleAddForm} disabled={loading}>
                                    {showAddForm ? "Ẩn Form" : "Thêm Danh Mục Mới"}
                                </button>
                            </div>
                            <div className="scm-list">
                                {Array.isArray(categories) && categories.length > 0 ? (
                                    categories.map((category) => (
                                        <div key={category._id} className="scm-card">
                                            <div className="scm-image">
                                                <img src={category.photo || "/placeholder.svg"} alt={`Ảnh ${category.name}`} />
                                            </div>
                                            <div className="scm-details">
                                                <h3>{category.name}</h3>
                                                <p><strong>Mô tả:</strong> {category.description}</p>
                                                <div className="scm-actions">
                                                    <button className="scm-view-button" onClick={() => handleViewDetails(category)} disabled={loading}>
                                                        Xem Chi Tiết
                                                    </button>
                                                    <button className="scm-delete-button" onClick={() => handleDelete(category._id)} disabled={loading}>
                                                        Xóa
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-categories">Không tìm thấy danh mục nào</div>
                                )}
                            </div>
                        </div>

                        {showAddForm && (
                            <div className="scm-add-section">
                                <div className="scm-add-card">
                                    <h2 className="scm-subheading">Thêm Danh Mục Mới</h2>
                                    <form onSubmit={handleSubmit} className="scm-form">
                                        <div className="scm-form-group">
                                            <label htmlFor="name">Tên Danh Mục</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="scm-form-group">
                                            <label htmlFor="description">Mô tả</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="4"
                                                required
                                            />
                                        </div>

                                        <div className="scm-form-group">
                                            <label htmlFor="photo">Ảnh Danh Mục</label>
                                            <input
                                                type="file"
                                                id="photo"
                                                name="photo"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                            <div className="scm-image-preview">
                                                <img src={formData.photo} alt="Xem trước ảnh danh mục" />
                                            </div>
                                        </div>

                                        <div className="scm-form-actions">
                                            <button type="button" className="scm-cancel-button" onClick={toggleAddForm}>
                                                Hủy
                                            </button>
                                            <button type="submit" className="scm-submit-button" disabled={loading}>
                                                {loading ? "Đang thêm..." : "Thêm Danh Mục"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="scm-detail-section">
                    <button className="scm-back-button" onClick={() => setShowDetail(false)} disabled={loading}>Quay Lại</button>
                    <div className="scm-detail-content">
                        <div className="scm-detail-image">
                            <img src={selectedCategory.photo || "/placeholder.svg"} alt={`Ảnh ${selectedCategory.name}`} />
                        </div>
                        <div className="scm-detail-info">
                            <h2>{selectedCategory.name}</h2>
                            <p><strong>Mô tả:</strong> {selectedCategory.description}</p>
                            <button className="scm-delete-button" onClick={() => { handleDelete(selectedCategory._id); setShowDetail(false); }} disabled={loading}>
                                Xóa
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
} 